import { logger } from '@/utils/logger';

/**
 * Type-safe local storage utility class
 * Handles JSON serialization/deserialization and error handling
 */
export class LocalStorageService {
  /**
   * Get an item from local storage
   * @param key - The storage key
   * @param defaultValue - Default value if key doesn't exist or parsing fails
   * @returns The parsed value or default value
   */
  static get<T>(key: string, defaultValue: T): T {
    if (!this.isAvailable()) {
      logger.warn('LocalStorage is not available');
      return defaultValue;
    }

    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return defaultValue;
      }
      return JSON.parse(item) as T;
    } catch (error) {
      logger.error(`Error reading from localStorage (key: ${key}):`, error);
      return defaultValue;
    }
  }

  /**
   * Set an item in local storage
   * @param key - The storage key
   * @param value - The value to store (will be JSON stringified)
   * @returns true if successful, false otherwise
   */
  static set<T>(key: string, value: T): boolean {
    if (!this.isAvailable()) {
      logger.warn('LocalStorage is not available');
      return false;
    }

    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      logger.error(`Error writing to localStorage (key: ${key}):`, error);
      return false;
    }
  }

  /**
   * Remove an item from local storage
   * @param key - The storage key to remove
   * @returns true if successful, false otherwise
   */
  static remove(key: string): boolean {
    if (!this.isAvailable()) {
      logger.warn('LocalStorage is not available');
      return false;
    }

    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      logger.error(`Error removing from localStorage (key: ${key}):`, error);
      return false;
    }
  }

  /**
   * Clear all items from local storage
   * @returns true if successful, false otherwise
   */
  static clear(): boolean {
    if (!this.isAvailable()) {
      logger.warn('LocalStorage is not available');
      return false;
    }

    try {
      localStorage.clear();
      return true;
    } catch (error) {
      logger.error('Error clearing localStorage:', error);
      return false;
    }
  }

  /**
   * Check if a key exists in local storage
   * @param key - The storage key to check
   * @returns true if the key exists, false otherwise
   */
  static has(key: string): boolean {
    if (!this.isAvailable()) {
      return false;
    }

    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      logger.error(`Error checking localStorage (key: ${key}):`, error);
      return false;
    }
  }

  /**
   * Get all keys in local storage
   * @returns Array of all storage keys
   */
  static keys(): string[] {
    if (!this.isAvailable()) {
      return [];
    }

    try {
      return Object.keys(localStorage);
    } catch (error) {
      logger.error('Error getting localStorage keys:', error);
      return [];
    }
  }

  /**
   * Check if localStorage is available
   * @returns true if localStorage is available, false otherwise
   */
  static isAvailable(): boolean {
    try {
      const testKey = '__localStorage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  }
}

/**
 * Storage keys used throughout the application
 * Centralized to avoid key collisions
 */
export const STORAGE_KEYS = {
  TRANSACTION_FILTERS: 'polybank_transaction_filters',
  BALANCE_VISIBILITY: 'polybank_balance_visibility',
} as const;
